/**
 * Utility for managing anonymous user IDs for public agents
 * Stores IDs in IndexedDB if persistence is enabled, otherwise in sessionStorage
 */

const INDEXED_DB_NAME = 'ai-central-anonymous-users';
const INDEXED_DB_VERSION = 1;
const STORE_NAME = 'users';

/**
 * Generate a UUID v4
 */
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create anonymous user ID from IndexedDB
 */
async function getOrCreateFromIndexedDB(agentId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(INDEXED_DB_NAME, INDEXED_DB_VERSION);
    
    request.onerror = () => reject(new Error('Failed to open IndexedDB'));
    
    request.onsuccess = () => {
      const db = request.result;
      
      // Check if store exists, if not create it
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        transaction.onerror = () => reject(new Error('Failed to create object store'));
        transaction.oncomplete = () => {
          // Retry after creating store
          getOrCreateFromIndexedDB(agentId).then(resolve).catch(reject);
        };
        return;
      }
      
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const key = `anonymous_user_${agentId}`;
      
      // Try to get existing ID
      const getRequest = store.get(key);
      
      getRequest.onsuccess = () => {
        if (getRequest.result) {
          resolve(getRequest.result.userId);
        } else {
          // Create new ID
          const userId = generateUUID();
          const putRequest = store.put({ userId }, key);
          
          putRequest.onsuccess = () => resolve(userId);
          putRequest.onerror = () => reject(new Error('Failed to store anonymous user ID'));
        }
      };
      
      getRequest.onerror = () => reject(new Error('Failed to read from IndexedDB'));
    };
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

/**
 * Get or create anonymous user ID from sessionStorage
 */
function getOrCreateFromSessionStorage(agentId: string): string {
  const key = `anonymous_user_${agentId}`;
  const existing = sessionStorage.getItem(key);
  
  if (existing) {
    return existing;
  }
  
  const userId = generateUUID();
  sessionStorage.setItem(key, userId);
  return userId;
}

/**
 * Get or create an anonymous user ID for a public agent
 * @param agentId - The agent ID
 * @param enablePersistence - Whether persistence is enabled (uses IndexedDB if true, sessionStorage if false)
 * @returns The anonymous user ID
 */
export async function getOrCreateAnonymousUserId(
  agentId: string,
  enablePersistence: boolean
): Promise<string> {
  if (enablePersistence) {
    try {
      return await getOrCreateFromIndexedDB(agentId);
    } catch (error) {
      console.warn('Failed to use IndexedDB for anonymous user ID, falling back to sessionStorage:', error);
      return getOrCreateFromSessionStorage(agentId);
    }
  } else {
    return getOrCreateFromSessionStorage(agentId);
  }
}

